import { ReactNode, useState } from "react";
import Button from "../../../student/components/generic/button";
import {
  DMModal,
  DMModalContent,
  DMModalFooter,
  DMModalTitle,
} from "../../../shared/DMModal";
import { useMutation } from "@tanstack/react-query";
import { executeQuery } from "../../../utils";
import { SearchTagData } from "../../types";

export const DeleteSearchTagsModal: React.FC<{
  onSave: () => void;
  searchDataToDelete: SearchTagData;
  children: (onClick: () => void) => ReactNode;
}> = ({ onSave, searchDataToDelete, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [nonFieldError, setNonFieldError] = useState<string | null>(null);

  const { mutate: deleteSearchTag, isPending: deletePending } = useMutation({
    mutationFn: async ({
      searchTag,
      type,
    }: {
      searchTag: string;
      type: "all" | "single";
    }) => {
      return executeQuery({
        path: `/manager_new/module/search_tags`,
        method: "delete",
        requestBody: {
          searchTag,
          type,
        },
      });
    },
  });

  const close = () => {
    setIsVisible(false);
    setNonFieldError(null);
  };

  const onSubmit = (type: "all" | "single") => {
    setNonFieldError(null);
    deleteSearchTag(
      { searchTag: searchDataToDelete.searchTag, type },
      {
        onSuccess: () => {
          onSave();
          close();
        },
        onError: () => {
          setNonFieldError("An error occurred while deleting the search tag");
        },
      }
    );
  };

  return (
    <>
      <div>{children(() => setIsVisible(true))}</div>

      <DMModal visible={isVisible} onClose={close}>
        <div className="flex flex-col gap-10">
          <DMModalTitle>Delete Search Tag</DMModalTitle>
          <DMModalContent>
            <>
              <div className="flex w-full flex-col gap-1">
                <h2 className="text-sm">Search Tag</h2>
                <p>{searchDataToDelete.searchTag}</p>
              </div>
              <div className="flex w-full flex-col gap-1">
                <h2 className="text-sm">Associations</h2>
                <p>{searchDataToDelete.associations.join(", ")}</p>
              </div>
              {nonFieldError && (
                <p className="w-full rounded bg-dm-error-100 px-4 py-2 text-center text-sm text-dm-error-500">
                  {nonFieldError}
                </p>
              )}
            </>
          </DMModalContent>
          <DMModalFooter>
            <Button type="outline" onClick={close} size="xsmall">
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => onSubmit("all")}
                className="bg-dm-error-200 !text-black hover:bg-dm-error-500 hover:!text-white active:bg-dm-error-600"
                size="xsmall"
                disabled={deletePending}
              >
                Delete From All Existing Skills
              </Button>
              <Button
                onClick={() => onSubmit("single")}
                className="bg-dm-error-500 text-white hover:bg-dm-error-600 active:bg-dm-error-800"
                size="xsmall"
                disabled={deletePending}
              >
                Delete
              </Button>
            </div>
          </DMModalFooter>
        </div>
      </DMModal>
    </>
  );
};
